import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { apiConstants, downloadZip } from '../../../common/constants';
import { getMerchant } from '../../../helpers/ReduxHelpers';
import { ABD_INCLUDES, buildBaselineAndFilter, buildBaselineOrFilter } from './constants';

export function exportXlsx(filename, sheets) {
    if (!Array.isArray(sheets) || sheets.length === 0) return;
    const wb = XLSX.utils.book_new();
    sheets.forEach((s) => {
        const rows = Array.isArray(s.rows) ? s.rows : [];
        if (rows.length === 0) {
            XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['No data']]), safeName(s.name));
            return;
        }
        const ws = s.header
            ? XLSX.utils.json_to_sheet(rows, { header: s.header })
            : XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, ws, safeName(s.name));
    });
    const stamp = format(new Date(), 'dd-MMM-yyyy_HH-mm');
    XLSX.writeFile(wb, `${filename}_${stamp}.xlsx`);
}

/** Saves a CSV response body (string, Blob or bytes) as `<fileName>_<stamp>.xlsx`. */
export async function saveCsvAsXlsx(data, fileName) {
    const csvText = typeof data === 'string'
        ? data
        : (data instanceof Blob ? await data.text() : new TextDecoder('utf-8').decode(data));
    const wb = XLSX.read(csvText, { type: 'string' });
    const stamp = format(new Date(), 'dd-MMM-yyyy_HH-mm');
    XLSX.writeFile(wb, `${fileName}_${stamp}.xlsx`);
}

// Same body as the list's /order/search call, so the file holds exactly the rows the screen counts.
function buildExportBody({ filters = {}, extraAndFilter = [], extraOrFilter = [], appointmentRequired = true }) {
    const merchantId = getMerchant()?.id || '';
    return {
        andfilter: [
            ...buildBaselineAndFilter(filters, merchantId, appointmentRequired),
            ...(Array.isArray(extraAndFilter) ? extraAndFilter : []),
        ],
        orfilter: [
            ...buildBaselineOrFilter(filters),
            ...(Array.isArray(extraOrFilter) ? extraOrFilter : []),
        ],
        offset: 0,
        limit: 50000,
        skipCount: true,
        includes: ABD_INCLUDES,
        sort: { field: 'awbRegisteredDate', direction: 'DESC' },
    };
}

/**
 * One workbook: the given summary sheets first, then one sheet of shipment rows per entry in `shipments`
 * ({ name, filters, extraAndFilter, appointmentRequired }), fetched from the same export as the lists.
 */
export async function exportReport({ fileName, sheets = [], shipments = [] }) {
    const wb = XLSX.utils.book_new();
    sheets.forEach((s) => {
        const rows = Array.isArray(s.rows) ? s.rows : [];
        const ws = rows.length ? XLSX.utils.json_to_sheet(rows, s.header ? { header: s.header } : undefined) : XLSX.utils.aoa_to_sheet([['No data']]);
        XLSX.utils.book_append_sheet(wb, ws, safeName(s.name));
    });
    for (const sh of shipments) {
        const res = await downloadZip(apiConstants.APPOINTMENT_DELIVERY_EXPORT, buildExportBody(sh));
        const data = res?.data;
        const csvText = !data ? '' : typeof data === 'string'
            ? data
            : (data instanceof Blob ? await data.text() : new TextDecoder('utf-8').decode(data));
        const csvWb = csvText.trim() ? XLSX.read(csvText, { type: 'string' }) : null;
        const ws = csvWb ? csvWb.Sheets[csvWb.SheetNames[0]] : XLSX.utils.aoa_to_sheet([['No shipments']]);
        XLSX.utils.book_append_sheet(wb, ws, safeName(sh.name));
    }
    const stamp = format(new Date(), 'dd-MMM-yyyy_HH-mm');
    XLSX.writeFile(wb, `${fileName}_${stamp}.xlsx`);
}

export async function exportFilteredCsv({
    filters = {},
    extraAndFilter = [],
    extraOrFilter = [],
    fileName = 'ABD_Export',
    enqueueSnackbar,
    appointmentRequired = true,
}) {
    try {
        const body = buildExportBody({ filters, extraAndFilter, extraOrFilter, appointmentRequired });
        enqueueSnackbar?.('Generating export…', { variant: 'info' });
        const res = await downloadZip(apiConstants.APPOINTMENT_DELIVERY_EXPORT, body);
        if (!res || !res.data) {
            const msg = 'Export failed — empty response';
            enqueueSnackbar?.(msg, { variant: 'error' });
            return { status: 'ERROR', message: msg };
        }
        await saveCsvAsXlsx(res.data, fileName);
        enqueueSnackbar?.('Export ready', { variant: 'success' });
        return { status: 'SUCCESS' };
    } catch (e) {
        let msg = e?.message || 'Export failed';
        const errBlob = e?.response?.data;
        if (errBlob && typeof errBlob.text === 'function') {
            try {
                const text = await errBlob.text();
                try {
                    const parsed = JSON.parse(text);
                    msg = parsed?.message || parsed?.exception || text || msg;
                } catch {
                    msg = text || msg;
                }
            } catch { /* use fallback msg */ }
        } else if (typeof errBlob === 'string') {
            msg = errBlob;
        } else if (errBlob?.message) {
            msg = errBlob.message;
        }
        enqueueSnackbar?.(msg, { variant: 'error' });
        return { status: 'ERROR', message: msg };
    }
}

function safeName(name) {
    return String(name || 'Sheet').replace(/[\\/?*[\]:]/g, '_').slice(0, 31);
}
